import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Image as ImageIcon, RefreshCw, AlertTriangle, CheckCircle2, ExternalLink, Lightbulb, Sun, Contrast, Ruler, Info, ChevronDown, ChevronUp, Upload, X, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const BRAND_BROWN = '#5D4E3C';
const BRAND_TAN = '#C4A084';

const ISSUE_META = {
  size: { label: 'Zu klein', icon: Ruler, color: '#D89B7B' },
  exposure: { label: 'Belichtung', icon: Sun, color: '#E0A75B' },
  contrast: { label: 'Kontrast', icon: Contrast, color: '#7BA98E' },
};

const PHOTO_CHEAT_SHEET = [
  { icon: Ruler, title: 'Auflösung', text: 'Ziel: mind. 1600 px längste Kante · ideal 2400–3200 px für Print + Retina.' },
  { icon: Sun, title: 'Licht', text: 'Fenster mit indirektem Tageslicht ≈ 30–45°. Direkte Sonne meiden, mit weißem Tuch streuen.' },
  { icon: Contrast, title: 'Hintergrund', text: 'Einheitlicher heller Hintergrund (weißer Karton oder helles Holz) — hebt Produkt hervor und erhöht Vertrauen.' },
  { icon: ImageIcon, title: 'Format', text: 'Am besten 4:5 (Portrait) oder 1:1 (Quadrat). Extreme Panoramen werden im Grid beschnitten.' },
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_e) {
    return iso;
  }
}

function KpiCard({ label, value, accent, icon: Icon, testId }) {
  return (
    <div
      className="rounded-none border-l-[3px] p-4 bg-white shadow-sm hover:shadow transition-shadow"
      style={{ borderLeftColor: accent }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-none text-white shrink-0"
          style={{ backgroundColor: accent }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
          <div className="text-2xl font-semibold" style={{ color: BRAND_BROWN }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

export default function ProductImageQualityReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showTips, setShowTips] = useState(true);
  // Replace-flow state
  const [uploadTarget, setUploadTarget] = useState(null); // {productId, oldUrl, productName}
  const [uploading, setUploading] = useState(false);
  const [compareResult, setCompareResult] = useState(null); // {reasons, old_metrics, new_metrics, pendingFile}
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const res = await axiosInstance.get('/admin/product-image-quality');
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden des Reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setRefreshing(true);
    try {
      const res = await axiosInstance.post('/admin/product-image-quality/refresh');
      setReport(res.data);
      toast.success(
        `Scan abgeschlossen: ${res.data.affected_products} Produkte betroffen · ${res.data.low_quality_images} zu klein · ${res.data.dark_images} zu dunkel · ${res.data.low_contrast_images} wenig Kontrast.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan fehlgeschlagen');
    } finally {
      setRefreshing(false);
    }
  };

  const openUploadFor = (productId, productName, oldUrl) => {
    setUploadTarget({ productId, productName, oldUrl });
    // Trigger the hidden file input on next tick
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const removeReplacedImageFromReport = (productId, oldUrl) => {
    setReport((prev) => {
      if (!prev) return prev;
      const products = prev.products
        .map((p) => {
          if (p.id !== productId) return p;
          return { ...p, flagged_images: p.flagged_images.filter((i) => i.url !== oldUrl) };
        })
        .filter((p) => p.flagged_images.length > 0);
      return { ...prev, products, affected_products: products.length };
    });
  };

  const submitReplace = async (file, force = false) => {
    if (!uploadTarget || !file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('product_id', uploadTarget.productId);
    formData.append('old_image_url', uploadTarget.oldUrl);
    formData.append('file', file);
    if (force) formData.append('force', 'true');
    try {
      const res = await axiosInstance.post('/admin/product-image-quality/replace', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.status === 'replaced') {
        toast.success(force ? 'Bild trotzdem ersetzt.' : 'Bild ersetzt — sichtbar besser.');
        removeReplacedImageFromReport(uploadTarget.productId, uploadTarget.oldUrl);
        setCompareResult(null);
        setUploadTarget(null);
      } else if (res.data.status === 'not_better') {
        // Present the compare dialog
        setCompareResult({
          reasons: res.data.reasons || [],
          old_metrics: res.data.old_metrics,
          new_metrics: res.data.new_metrics,
          pendingFile: file,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-pick same file
    if (f) submitReplace(f, false);
  };

  const cancelReplace = () => {
    setCompareResult(null);
    setUploadTarget(null);
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="image-quality-loading">
        <div className="text-sm text-neutral-500">Bildqualitäts-Report wird geladen…</div>
      </div>
    );
  }

  const products = report?.products || [];
  const minEdge = report?.min_edge_px || 1200;

  return (
    <div className="p-6" data-testid="image-quality-report-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light" style={{ color: BRAND_BROWN, letterSpacing: '0.02em' }}>
            Produkt-Bildqualität
          </h1>
          <p className="mt-1 text-sm text-neutral-600 max-w-xl">
            Automatische Diagnose aller aktiven Produktbilder: Auflösung, Belichtung, Kontrast — jeweils mit konkretem Verbesserungs-Tipp. Ziel: mind. <strong>{minEdge}px</strong> längste Kante.
          </p>
          <p className="mt-2 text-xs" style={{ color: '#8B7355' }}>
            <Upload className="h-3 w-3 inline-block mr-1 -mt-0.5" />
            <strong>Tipp:</strong> Mit der Maus über ein bemängeltes Bild fahren und klicken → neues Bild hochladen. Das System prüft automatisch, ob es besser ist.
          </p>
          <div className="mt-1 text-xs text-neutral-500">
            Letzter Scan: {formatDate(report?.generated_at)}
          </div>
        </div>

        <Button
          onClick={runScan}
          disabled={refreshing}
          className="rounded-none text-white"
          style={{ backgroundColor: BRAND_BROWN, letterSpacing: '2px', fontSize: 12, textTransform: 'uppercase' }}
          data-testid="image-quality-scan-btn"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Scanne…' : 'Jetzt scannen'}
        </Button>
      </div>

      {/* Foto-Cheat-Sheet */}
      <Card className="rounded-none border-2 mb-6" style={{ borderColor: BRAND_TAN, backgroundColor: '#FBF6EE' }} data-testid="photo-cheat-sheet">
        <CardContent className="p-4">
          <button
            type="button"
            onClick={() => setShowTips((v) => !v)}
            className="w-full flex items-center justify-between text-left"
            data-testid="cheat-sheet-toggle"
          >
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" style={{ color: BRAND_BROWN }} />
              <span className="text-sm font-medium" style={{ color: BRAND_BROWN, letterSpacing: '0.02em' }}>
                Foto-Cheat-Sheet · so bekommst du druckreife Bilder
              </span>
            </div>
            {showTips ? <ChevronUp className="h-4 w-4" style={{ color: BRAND_BROWN }} /> : <ChevronDown className="h-4 w-4" style={{ color: BRAND_BROWN }} />}
          </button>
          {showTips && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
              {PHOTO_CHEAT_SHEET.map((tip) => {
                const Ic = tip.icon;
                return (
                  <div key={tip.title} className="bg-white p-3 border" style={{ borderColor: '#EBDCC5' }}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Ic className="h-3.5 w-3.5" style={{ color: BRAND_TAN }} />
                      <div className="text-[11px] uppercase tracking-widest" style={{ color: BRAND_BROWN }}>{tip.title}</div>
                    </div>
                    <div className="text-xs leading-relaxed" style={{ color: '#6B5D4F' }}>{tip.text}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard
          label="Produkte geprüft"
          value={report?.total_products_scanned ?? 0}
          accent={BRAND_TAN}
          icon={CheckCircle2}
          testId="kpi-scanned"
        />
        <KpiCard
          label="Bilder total"
          value={report?.total_images ?? 0}
          accent="#7BA98E"
          icon={ImageIcon}
          testId="kpi-total-images"
        />
        <KpiCard
          label={`< ${minEdge}px`}
          value={report?.low_quality_images ?? 0}
          accent="#D89B7B"
          icon={Ruler}
          testId="kpi-low-quality"
        />
        <KpiCard
          label="Zu dunkel"
          value={report?.dark_images ?? 0}
          accent="#7A5D3D"
          icon={Sun}
          testId="kpi-dark"
        />
        <KpiCard
          label="Wenig Kontrast"
          value={report?.low_contrast_images ?? 0}
          accent="#B7A87A"
          icon={Contrast}
          testId="kpi-contrast"
        />
        <KpiCard
          label="Produkte betroffen"
          value={report?.affected_products ?? 0}
          accent="#B75757"
          icon={AlertTriangle}
          testId="kpi-affected"
        />
      </div>

      {/* Empty state */}
      {(!report?.generated_at) && (
        <Card className="rounded-none border-2" style={{ borderColor: BRAND_TAN }}>
          <CardContent className="p-8 text-center">
            <ImageIcon className="mx-auto mb-3 h-10 w-10" style={{ color: BRAND_TAN }} />
            <div className="text-sm text-neutral-700">
              Noch kein Scan durchgeführt. Klicke oben auf <strong>&bdquo;Jetzt scannen&ldquo;</strong>,
              um alle Produktbilder zu vermessen.
            </div>
          </CardContent>
        </Card>
      )}

      {report?.generated_at && products.length === 0 && (
        <Card className="rounded-none border-2" style={{ borderColor: '#7BA98E' }}>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
            <div className="text-base font-medium" style={{ color: BRAND_BROWN }}>
              Alle Produktbilder sind größer als {minEdge}px 🎉
            </div>
            <div className="mt-1 text-sm text-neutral-600">
              Deine Bildqualität ist druckreif und website-tauglich.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products list */}
      {products.length > 0 && (
        <div className="space-y-4" data-testid="low-quality-product-list">
          {products.map((p) => (
            <Card key={p.id} className="rounded-none border" data-testid={`low-quality-product-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-neutral-500">
                      {p.category || 'Ohne Kategorie'}
                      {p.sku ? ` · SKU ${p.sku}` : ''}
                    </div>
                    <div className="font-medium mt-0.5" style={{ color: BRAND_BROWN }}>
                      {p.name}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-none" style={{ backgroundColor: '#D89B7B', color: 'white' }}>
                      {p.flagged_images.length} Bild{p.flagged_images.length === 1 ? '' : 'er'}
                    </Badge>
                    <Link
                      to={`/admin/products?edit=${p.id}`}
                      className="text-xs inline-flex items-center gap-1 underline"
                      style={{ color: BRAND_BROWN }}
                      data-testid={`edit-product-${p.id}`}
                    >
                      Bearbeiten <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {p.flagged_images.map((img, i) => (
                    <div key={img.url + i} className="border bg-white p-3" style={{ borderColor: '#E5D9C8' }} data-testid={`flagged-image-${p.id}-${i}`}>
                      <div className="flex gap-3">
                        <div className="relative shrink-0 group">
                          <a href={img.url} target="_blank" rel="noreferrer">
                            <img
                              src={img.url}
                              alt=""
                              className="w-24 h-24 object-cover border"
                              style={{ borderColor: '#E5D9C8' }}
                              onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                            />
                          </a>
                          <button
                            type="button"
                            onClick={() => openUploadFor(p.id, p.name, img.url)}
                            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ backgroundColor: 'rgba(93, 78, 60, 0.85)' }}
                            title="Klicken, um dieses Bild zu ersetzen"
                            data-testid={`replace-image-btn-${p.id}-${i}`}
                          >
                            <div className="text-white text-[10px] uppercase tracking-widest flex flex-col items-center gap-1">
                              <Upload className="h-4 w-4" />
                              Ersetzen
                            </div>
                          </button>
                        </div>
                        <div className="min-w-0 flex-1">
                          {/* Metric chips */}
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {img.width > 0 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 border" style={{ borderColor: '#E5D9C8', color: '#6B5D4F' }}>
                                {img.width}×{img.height}
                              </span>
                            )}
                            {img.luminance != null && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 border inline-flex items-center gap-1"
                                style={{
                                  borderColor: '#E5D9C8',
                                  color: img.luminance < 90 ? '#7A5D3D' : img.luminance > 220 ? '#B75757' : '#6B5D4F',
                                }}
                                title="Mittlere Helligkeit 0–255"
                              >
                                <Sun className="h-2.5 w-2.5" /> {img.luminance}
                              </span>
                            )}
                            {img.contrast_std != null && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 border inline-flex items-center gap-1"
                                style={{
                                  borderColor: '#E5D9C8',
                                  color: img.contrast_std < 32 ? '#B75757' : '#6B5D4F',
                                }}
                                title="Kontrast (Standardabweichung)"
                              >
                                <Contrast className="h-2.5 w-2.5" /> σ{img.contrast_std}
                              </span>
                            )}
                          </div>
                          {/* Issue badges */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(img.issues || []).map((issue) => {
                              const meta = ISSUE_META[issue];
                              if (!meta) return null;
                              const Ic = meta.icon;
                              return (
                                <span
                                  key={issue}
                                  className="text-[10px] uppercase tracking-wider text-white px-2 py-0.5 inline-flex items-center gap-1"
                                  style={{ backgroundColor: meta.color, letterSpacing: '1.5px' }}
                                >
                                  <Ic className="h-2.5 w-2.5" /> {meta.label}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Tips list */}
                      {img.tips && img.tips.length > 0 && (
                        <ul className="mt-3 pt-3 space-y-2 border-t" style={{ borderColor: '#F0E6D6' }} data-testid={`image-tips-${p.id}-${i}`}>
                          {img.tips.map((tip, ti) => (
                            <li key={ti} className="text-[12px] leading-snug flex gap-2" style={{ color: '#5D4E3C' }}>
                              <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: '#D89B7B' }} />
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Hidden file input triggered by clicking any image thumbnail */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onFileChosen}
        data-testid="image-replace-file-input"
      />

      {/* Compare dialog when new image isn't actually better */}
      <Dialog open={!!compareResult} onOpenChange={(open) => !open && cancelReplace()}>
        <DialogContent className="max-w-2xl rounded-none" style={{ borderColor: '#D89B7B', borderWidth: 2 }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: BRAND_BROWN }}>
              <AlertTriangle className="h-5 w-5" style={{ color: '#D89B7B' }} />
              Neues Bild ist nicht besser
            </DialogTitle>
            <DialogDescription className="text-sm">
              {uploadTarget?.productName && (
                <span className="text-neutral-700">Produkt: <strong>{uploadTarget.productName}</strong>. </span>
              )}
              Die automatische Prüfung hat folgende Punkte gefunden:
            </DialogDescription>
          </DialogHeader>

          {compareResult && (
            <div>
              {/* Reasons */}
              <ul className="mt-2 mb-4 space-y-1.5" data-testid="compare-reasons">
                {compareResult.reasons.map((r, i) => (
                  <li key={i} className="text-sm flex gap-2" style={{ color: '#5D4E3C' }}>
                    <X className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#B75757' }} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>

              {/* Metrics compare */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
                <MetricsPanel title="Aktuelles Bild" metrics={compareResult.old_metrics} accent="#8B7355" />
                <ArrowRight className="hidden sm:block mx-auto" style={{ color: BRAND_TAN }} />
                <MetricsPanel title="Neues Bild" metrics={compareResult.new_metrics} accent="#D89B7B" />
              </div>

              <div className="mt-4 p-3 text-xs" style={{ background: '#FAF5EE', color: '#5D4E3C' }}>
                <strong>Was du jetzt tun kannst:</strong><br />
                <span className="opacity-90">
                  · <em>Anderes Bild wählen:</em> nimm ein hochwertigeres Foto (Tipps siehe Cheat-Sheet oben).<br />
                  · <em>Trotzdem ersetzen:</em> wenn du sicher bist, dass das neue Bild besser passt (z. B. neues Motiv), kannst du das erzwingen.
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="rounded-none"
              onClick={cancelReplace}
              disabled={uploading}
              data-testid="compare-cancel-btn"
            >
              Anderes Bild wählen
            </Button>
            <Button
              className="rounded-none text-white"
              style={{ backgroundColor: '#D89B7B' }}
              onClick={() => compareResult?.pendingFile && submitReplace(compareResult.pendingFile, true)}
              disabled={uploading}
              data-testid="compare-force-btn"
            >
              {uploading ? 'Ersetze…' : 'Trotzdem ersetzen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricsPanel({ title, metrics, accent }) {
  if (!metrics) return null;
  return (
    <div className="border p-3" style={{ borderColor: '#E5D9C8', backgroundColor: 'white' }}>
      <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: accent, letterSpacing: '2px' }}>{title}</div>
      <div className="space-y-1 text-xs" style={{ color: '#5D4E3C' }}>
        <div className="flex justify-between"><span className="text-neutral-500">Auflösung</span><span className="font-mono">{metrics.width}×{metrics.height} px</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Helligkeit</span><span className="font-mono">{metrics.luminance ?? '–'} / 255</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Kontrast (σ)</span><span className="font-mono">{metrics.contrast_std ?? '–'}</span></div>
      </div>
    </div>
  );
}
